<script lang="ts" module>
  // CheckboxGroup — the design-system multi-select primitive
  // (wiki/feature-design-system.md §Component primitives). A 0–N checkbox group
  // over a typed option list, composed from the Checkbox primitive. The option
  // set is a typed prop (value/label/optional description), never a slot; the
  // selection is a typed `string[]` committed via `bind:value`. A `<fieldset>`
  // with a `<legend>` gives it a correct group label for assistive tech. No
  // bespoke per-page checkbox-group chrome — the framework-lens picker and any
  // future multi-select compose this.
  export interface CheckboxOption {
    /** The value added to / removed from the bound selection array. */
    value: string;
    /** The visible label. */
    label: string;
    /** Optional one-line description shown under the label. */
    description?: string;
  }
</script>

<script lang="ts">
  import Checkbox from '$lib/ui/primitives/Checkbox.svelte';

  interface Props {
    /** The selectable options (0 or more). */
    options: CheckboxOption[];
    /** The selected values (bindable). Order follows toggle order. */
    value?: string[];
    /** Fired after the selection changes, with the new selection array. */
    onChange?: (value: string[]) => void;
    /** Group label rendered as the fieldset legend. */
    legend: string;
    /** Hide the legend visually (still read by assistive tech). */
    legendHidden?: boolean;
    /** Stable id prefix for the per-row inputs. */
    idPrefix?: string;
    /** Disable the whole group. */
    disabled?: boolean;
    class?: string;
  }

  let {
    options,
    value = $bindable([]),
    onChange,
    legend,
    legendHidden = false,
    idPrefix = 'cbg',
    disabled = false,
    class: klass = '',
  }: Props = $props();

  function isChecked(v: string): boolean {
    return value.includes(v);
  }

  function toggle(v: string, checked: boolean): void {
    if (disabled) return;
    // Rebuild a NEW array (preserve order: keep existing, append on add) so the
    // bound value is a fresh reference — Svelte 5 reactivity + parent $derived
    // both see the change.
    const next = checked
      ? value.includes(v)
        ? value
        : [...value, v]
      : value.filter((x) => x !== v);
    value = next;
    onChange?.(next);
  }
</script>

<fieldset class="vd-cbg {klass}" disabled={disabled || undefined}>
  <legend class="vd-cbg__legend" class:vd-cbg__legend--hidden={legendHidden}>{legend}</legend>
  <div class="vd-cbg__list">
    {#each options as option (option.value)}
      <Checkbox
        id={`${idPrefix}-${option.value}`}
        label={option.label}
        description={option.description}
        checked={isChecked(option.value)}
        {disabled}
        onChange={(checked) => toggle(option.value, checked)}
      />
    {/each}
  </div>
</fieldset>

<style lang="scss">
  .vd-cbg {
    border: 0;
    margin: 0;
    padding: 0;
    min-inline-size: 0;
  }

  .vd-cbg__legend {
    padding: 0;
    margin: 0 0 0.5rem;
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--ink-mute);
  }

  .vd-cbg__legend--hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  .vd-cbg__list {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }
</style>
